import { Trash2, Wrench } from 'lucide-react';

import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useConnectorToolLogo } from 'pages/tools/use-connector-tool-logo';
import { useTranslation } from 'react-i18next';

type AgentConfigToolCardTool = {
  id: string;
  name: string;
  description?: string;
  type?: ITool['type'];
  providers?: string[];
  composio_config?: ITool['composio_config'];
  ComposioConfig?: ITool['ComposioConfig'];
  open_connector_config?: ITool['open_connector_config'];
  OpenConnectorConfig?: ITool['OpenConnectorConfig'];
};

type AgentConfigToolCardProps = {
  tool: AgentConfigToolCardTool;
  onRemove: (toolId: string) => void;
  disabled?: boolean;
};

function ToolCardIcon({ tool }: { tool: AgentConfigToolCardTool }) {
  const logo = useConnectorToolLogo(tool as ITool);

  return (
    <div
      className={cn(
        'flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded',
        logo
          ? 'border border-grey-100 bg-true-white p-1.5 dark:border-border dark:bg-card'
          : 'bg-primary/10'
      )}
    >
      {logo ? (
        <img src={logo} alt={tool.name} className="h-full w-full object-contain" />
      ) : (
        <Wrench className="h-5 w-5" />
      )}
    </div>
  );
}

export function AgentConfigToolCard({ tool, onRemove, disabled = false }: AgentConfigToolCardProps) {
  const { t } = useTranslation();
  return (
    <div
      className={`flex min-w-0 items-center justify-between gap-3 rounded-lg border p-4 ${disabled ? 'opacity-50' : ''}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <ToolCardIcon tool={tool} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-foreground">{tool.name}</div>
          <div className="line-clamp-1 text-sm text-muted-foreground">
            {tool.description || t('NO_DESCRIPTION')}
          </div>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={() => onRemove(tool.id)}
        disabled={disabled}
        className="shrink-0 text-muted-foreground hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
